<script>
  import Modal from "@components/shared/modal.svelte";
  import Button from "@components/button.svelte";
  import DateRange from "@components/shared/date-range.svelte";
  import { Turbo } from "@hotwired/turbo-rails";
  import { CalendarX, Trash2, CheckCheck } from "lucide-svelte";
  import { get, post } from "@lib/requests";
  import { isAfter, isValid, eachDayOfInterval, endOfMonth, format } from "date-fns";
  import { fr } from "date-fns/locale";
  import { getClinics, currentClinic, currentUser, formatDateISO, formatDate, showAlert, getVets } from "@lib/utils";
  import useFormData from "@hooks/use-form-data.svelte.js";
  import fill from 'fill-range'

  let { 
    open = $bindable(false),
    vetId,
    clinicId,
    startDate,
    endDate,
    startTime,
    endTime,
    days = [],
    notes,
    onCreate,
    showButton = true,
  } = $props();

  let clinics = getClinics()
  let vets = $state({ loading: false, items: getVets() })

  let loading = $state(false)
  let userAbsences = $state([])
  let blockedEvents = $state([])
  let createdEvents = $state([])
  let error = $state(null)

  let daysOfWeek = [
    { label: "lundi", value: 1 },
    { label: "mardi", value: 2 },
    { label: "mercredi", value: 3 },
    { label: "jeudi", value: 4 },
    { label: "vendredi", value: 5 },
    { label: "samedi", value: 6 },
    { label: "dimanche", value: 0 },
  ]

  const form = useFormData({
    // NOTE: parentheses matter here; without them `vetId || currentUser()?.vet ? ...` will ignore `vetId`
    vet_id: vetId ?? (currentUser()?.vet ? currentUser()?.id : null),
    clinic_id: clinicId ?? currentClinic()?.id ?? null,
    start_date: formatDateISO(startDate || new Date()),
    end_date: formatDateISO(endDate || endOfMonth(new Date(), 1)),
    start_time: startTime || "",
    end_time: endTime || "",
    days_of_week: days || [],
    notes: notes || "",
    create_presences: true,
  })

  // Build a string from the following keys, vet_id, clinic_id, start_date, end_date, start_time, end_time, days_of_week
  let searchParams = $derived(JSON.stringify([
    form.data.vet_id,
    form.data.clinic_id,
    form.data.start_date,
    form.data.end_date,
    form.data.start_time,
    form.data.end_time,
    form.data.days_of_week,
  ]))

  let requiredFilled = $derived.by(() => {
    if (!form.data.days_of_week.length) return false
    let requiredFields = ["vet_id", "clinic_id", "start_date", "end_date", "start_time", "end_time", "days_of_week"]
    return requiredFields.every(field => form.data[field])
  })

  let datesValid = $derived.by(() => {
    if (!form.data.start_date || !form.data.end_date) return false
    if (!isValid(new Date(form.data.start_date)) || !isValid(new Date(form.data.end_date))) return false
    return !isAfter(form.data.start_date, form.data.end_date)
  })

  let timesValid = $derived.by(() => {
    if (!form.data.start_time || !form.data.end_time) return false
    return form.data.end_time > form.data.start_time
  })

  let vet = $derived(form.data.vet_id ? vets.items.find(vet => String(vet.id) === String(form.data.vet_id)) : null)

  let valid = $derived.by(() => {
    return requiredFilled && datesValid && timesValid
  })

  let disabled = $derived(!valid || !!createdEvents.length)

  let invalidEndTime = $derived(form.data.start_time && form.data.end_time && form.data.end_time <= form.data.start_time)

  let selectedDates = $derived.by(() => {
    if (!datesValid || !form.data.days_of_week.length) return []
    const allDates = eachDayOfInterval({ start: form.data.start_date, end: form.data.end_date })
    return allDates.filter(date => form.data.days_of_week.includes(date.getDay()))
  })

  let safeSelectedDates = $derived.by(() => {
    return selectedDates.filter(date => !userAbsences.some(absence => absence.date === formatDateISO(date)))
  })

  async function fetchVets() {
    try {
      vets.loading = true
      const response = await get(`/api/v1/clinics/${form.data.clinic_id}/vets`, { responseKind: "json" })
      const data = await response.json
      vets.items = data
    } catch (error) {
      vets.items = []
      showAlert(error.message || "Erreur lors de la récupération des vétérinaires", { type: "error" })
    } finally {
      vets.loading = false
    }
  }

  async function searchUserAbsences() {
    try {
      loading = true
      const body = { user_id: form.data.vet_id, start_date: form.data.start_date, end_date: form.data.end_date }
      const response = await post("/api/v1/user_absences/search", { responseKind: "json", body })
      const data = await response.json
      if (!response.ok) {
        userAbsences = []
      } else {
        userAbsences = data.absences
      }
    } catch (error) {
      userAbsences = []
      showAlert(error.message || "Erreur lors de la recherche des absences", { type: "error" })
    } finally {
      loading = false
    }
  }

  async function searchBlockedEvents() {
    try {
      loading = true
      const response = await post("/api/v1/blocked_events/search", { responseKind: "json", body: form.data })
      const data = await response.json
      if (!response.ok) {
        blockedEvents = []
      } else {
        blockedEvents = data.blocked_events
      }
    } catch (error) {
      blockedEvents = []
      showAlert(error.message || "Erreur lors de la recherche des créneaux bloqués", { type: "error" })
    } finally {
      loading = false
    }
  }

  function selectDay(value, e) {
    if (!e.shiftKey) return
    const newDays = fill(form.data.days_of_week[form.data.days_of_week.length - 1], value)
    const days = [...form.data.days_of_week, ...newDays]
    form.data.days_of_week = Array.from(new Set(days))
  }
  
  async function handleCreate() {
    if (!window.confirm(`Êtes-vous sûr de vouloir créer ${safeSelectedDates.length} créneaux bloqués pour ${vet?.name} ?`)) return
    try {
      loading = true
      const response = await post("/api/v1/blocked_events/batch_create", { responseKind: "json", body: form.data })
      const data = await response.json
      if (response.ok) {
        createdEvents = data.created
      } else {
        error = data.error
      }
    } catch (error) {
      error = error.message || "Erreur lors de la création des créneaux bloqués"
    } finally {
      loading = false
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    handleCreate()
  }

  function handleClose() {
    open = false
    form.reset()
    createdEvents = []
    error = null
    onCreate?.() || refreshCalendar()
  }

  function refreshCalendar() {
    const frame = document.getElementById("calendar")
    if (frame && typeof frame.reload === "function") return frame.reload()
    Turbo.visit(location.href, { action: "replace" })
  }

  // The only "prop" that should change is notes which is the only editable field in the consultation form
  $effect(() => {
    form.data.notes = notes
  })

  // We are building up a dependency string of the params that will effect the search results (eg: notes doesn't affect the search results)
  $effect(async () => {
    if (open && searchParams && valid) {
      await searchUserAbsences()
      await searchBlockedEvents()
    }
  })

  $effect(async () => {
    if (form.data.clinic_id) {
      await fetchVets()
    }
  })

</script>

{#if showButton}
  <Button naked text="Créneaux bloqués" onclick={() => open = true} class="flex-row menu-item cursor-pointer">
    <CalendarX size={16} class="text-gray-400 mr-2" />
    Créneaux bloqués
  </Button>
{/if}

<Modal bind:open title="Créer des créneaux bloqués" width="50rem" onClose={handleClose}>

  {#if error}
    <div class="app-message error">
      <i class="fal fa-exclamation-triangle"></i>
      <p>{error}</p>
    </div>
  {/if}

  {#if createdEvents.length > 0}
    <div class="app-message success">
      <i class="fal fa-check-circle"></i>
      <p>{createdEvents.length} créneau{createdEvents.length > 1 ? "s" : ""} bloqué{createdEvents.length > 1 ? "s" : ""} avec succès</p>
    </div>
  
  {:else}

    <form id="blocked-event-assignation-form" onsubmit={handleSubmit}>

      <div class="form-items">

        <!-- Clinic selection -->
        <div class="form-item col-span-6">
          <label for="clinic">Clinique</label>
          <select name="clinic" id="clinic" class="select" bind:value={form.data.clinic_id} required>
            <option value=""></option>
            {#each clinics as clinic}
              <option value={clinic.id}>{clinic.name}</option>
            {/each}
          </select>
        </div>
        
        <!-- User selection -->
        <div class="form-item col-span-6">
          <label for="vet">Vétérinaire</label>
          <select name="vet" id="vet" class="select" class:opacity-50={vets.loading} bind:value={form.data.vet_id} required>
            <option value=""></option>
            {#each vets.items as vet (vet.id)}
              <option value={vet.id}>{vet.name}</option>
            {/each}
          </select>
        </div>

        <!-- Slot time start -->
        <div class="form-item col-span-6">
          <label for="start_time">Début du créneau</label>
          <input type="time" name="start_time" id="start_time" class="input" bind:value={form.data.start_time} disabled={form.data.delete_all} required />
        </div>

        <!-- Slot time end -->
        <div class="form-item col-span-6">
          <label for="end_time">Fin du créneau</label>
          <input type="time" name="end_time" id="end_time" class="input" bind:value={form.data.end_time} disabled={form.data.delete_all} required />
          {#if invalidEndTime}
            <span class="error">La fin du créneau doit être après ou égal au début</span>
          {/if}
        </div>

        <!-- Days of the week  -->
        <div class="form-item col-span-12">
          <label for="days_of_week">Jours de la semaine</label>
          <div class="flex justify-between items-center mt-4">
            <div class="flex flex-wrap gap-4">
              {#each daysOfWeek as day}
                <label for={day.label} class="flex items-center gap-2" onclick={(e) => selectDay(day.value, e)}>
                  {day.label}
                  <input type="checkbox" name="days_of_week[]" value={day.value} id={day.label} bind:group={form.data.days_of_week} />
                </label>
              {/each}
            </div>
            <div class="flex gap-1 items-center">
              <Button naked iconSize={12} iconClass="text-gray-500" Icon={Trash2} hint="Effacer" onclick={() => form.data.days_of_week = []} />
              <Button naked iconSize={12} iconClass="text-gray-500" Icon={CheckCheck} hint="Tout sélectionner" onclick={() => form.data.days_of_week = daysOfWeek.map(day => day.value)} />
            </div>
          </div>
        </div>

        <!-- Date range -->
        <DateRange 
          bind:start={form.data.start_date} 
          bind:end={form.data.end_date} 
          labels={["Effectif à partir du", "Effectif jusqu'au"]}
          options="future" 
          class="col-span-12" 
          disabled={loading}
        />

        {#if valid}
          
          <!-- Selected dates -->          
          <div class="form-item col-span-12">
            <label for="selected-dates">Créneaux sélectionnés</label>
            <div class="p-4 border rounded text-xs text-slate-600 flex items-center gap-1 border-blue-100 bg-blue-50/70">
              <details class="overflow-y-auto max-h-[200px] w-full" open>
                <summary class="text-slate-700 font-medium hover:underline cursor-pointer">
                  {#if loading}
                    <span class="loading">Chargement...</span>
                  {:else}
                    {safeSelectedDates.length} créneaux sélectionnés
                    {#if userAbsences?.length > 0}
                      <small class="italic">({userAbsences.length} absences pour cette période qui ne pourront pas être bloqués)</small>
                    {/if}
                  {/if}
                </summary>
                <ul class="flex flex-col gap-1 flex-wrap text-xs text-slate-600 mt-2">
                  {#each safeSelectedDates as date}
                    <li class="italic text-slate-500">
                      {format(date, "EEEE d MMMM yyyy", { locale: fr }).toLowerCase()}
                      <span class="text-slate-400">&mdash;</span>
                      {form.data.start_time} - {form.data.end_time}
                    </li>
                  {/each}
                </ul>
              </details>
            </div>
          </div>

        {/if}

        <!-- Options -->
        <div class="form-item col-span-12">
          <label for="options">Options</label>
          <label class="checkbox mt-2 gap-2" for="create_presences">
            Créer des présences pour les jours pas encore marqués présents
            <input type="checkbox" name="create_presences" id="create_presences" bind:checked={form.data.create_presences} />
          </label>
        </div>

        <!-- Title/notes -->
        <div class="form-item col-span-12">
          <label for="notes">Notes</label>
          <textarea name="notes" id="notes" class="textarea" bind:value={form.data.notes}></textarea>
        </div>
        
      </div>
      
    </form>

  {/if}

  {#snippet footerButtons()}
    <Button 
      type="submit"
      class="button outline-submit" 
      text="Bloquer créneaux"
      form="blocked-event-assignation-form" 
      {disabled} 
      {loading}
      onclick={handleSubmit}
      Icon={CalendarX} />
  {/snippet}
  
</Modal>